import { SortableHeader } from '@/components/search/SortableHeader'
import { useTableSort } from '@/components/search/useTableSort'
import { lineSortValues, rateSortValues } from './billingSort'
import { groupLabel } from '@/components/usergroup/group'
import React, { useState } from 'react'
import { Configuration, Rate, Statement, money } from './billingModel'
import { Notices, Totals } from './billingParts'

type Input = { start: string; end: string; groupRates: Record<string, number>; vatBasisPoints: number; paymentDays: number; note: string }
type Defaults = { input: Input; groups: Rate[]; timeZone: string }
type Preview = { statement: Statement; fingerprint: string; overlaps: { id: string; start: string; end: string; source: string }[] }
type Props = {
  api: <T>(path: string, method?: string, body?: unknown) => Promise<T>
  configuration: Configuration
  onCancel: () => void
  onSaved: (statement: Statement) => Promise<void>
}

const localDate = (value: Date, zone: string) => new Intl.DateTimeFormat('sv-SE', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(value)
export const exclusiveEnd = (value: string) => {
  const d = new Date(value + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}
const requestID = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 15) | 64
  bytes[8] = (bytes[8] & 63) | 128
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export const BillingWizard = ({ api, configuration, onCancel, onSaved }: Props) => {
  const zone = configuration.settings.timeZone
  const today = localDate(new Date(), zone)
  const first = localDate(new Date(configuration.historyFrom), zone)
  const [from, setFrom] = useState(first)
  const [to, setTo] = useState(today)
  const [step, setStep] = useState(1)
  const [defaults, setDefaults] = useState<Defaults>()
  const [input, setInput] = useState<Input>()
  const [preview, setPreview] = useState<Preview>()
  const [saveID, setSaveID] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const ratesSort = useTableSort(defaults?.groups || [], { ...rateSortValues, rateCents: row => input?.groupRates[row.id] })
  const linesSort = useTableSort(preview?.statement.lines || [], lineSortValues)
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError('')
    try { await fn() } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      if ((e as { status?: number }).status === 409) { setPreview(undefined); setStep(2) }
    } finally { setBusy(false) }
  }
  const change = (patch: Partial<Input>) => { if (input) setInput({ ...input, ...patch }); setPreview(undefined); setSaveID('') }
  const cancel = () => { if (input && !window.confirm('Odrzucić niezapisane rozliczenie?')) return; onCancel() }
  const steps = ['Okres', 'Ustawienia', 'Podgląd i zapis']
  return <div className='billing-page'>
    <header className='billing-header'>
      <div>
        <h1>Nowe rozliczenie na żądanie</h1>
        <p className='billing-subtitle'>Rozliczenie za wybrany okres z własnymi stawkami. Nie zmienia ustawień automatycznych.</p>
      </div>
      <button disabled={busy} onClick={cancel}>Anuluj</button>
    </header>
    <ol className='billing-steps' aria-label='Etapy tworzenia rozliczenia'>
      {steps.map((name, i) => <li key={name} className={step > i + 1 ? 'billing-step-done' : undefined} aria-current={step === i + 1 ? 'step' : undefined}>{name}</li>)}
    </ol>
    <Notices error={error} busy={busy} />
    {step === 1 && <section>
      <div className='billing-card-head'><h2>1. Wybierz okres</h2></div>
      <p className='billing-hint'>Obie daty obejmują wskazany dzień. Historia jest dostępna od {first}. Możesz uwzględnić dzisiaj; jego wyliczenie obejmuje przypisania znane w chwili przeliczenia.</p>
      <form onSubmit={e => { e.preventDefault(); void run(async () => {
        const d = await api<Defaults>('/calculations/defaults', 'POST', { start: from, end: exclusiveEnd(to) })
        setDefaults(d); setInput(d.input); setPreview(undefined); setSaveID(''); setStep(2)
      }) }}>
        <div className='billing-settings' style={{ maxWidth: 520 }}>
          <label>Od<input type='date' required min={first} max={to || today} value={from} disabled={busy} onChange={e => setFrom(e.target.value)} /></label>
          <label>Do (włącznie)<input type='date' required min={from || first} max={today} value={to} disabled={busy} onChange={e => setTo(e.target.value)} /></label>
        </div>
        <div className='billing-actions billing-actions-end'><button className='billing-btn-primary' type='submit' disabled={busy}>Dalej</button></div>
      </form>
    </section>}
    {step === 2 && input && defaults && <section>
      <div className='billing-card-head'><h2>2. Ustaw stawki i parametry</h2></div>
      <ul className='billing-facts'>
        <li><span>Okres (włącznie)</span><strong>{from} – {to}</strong></li>
        <li><span>Waluta</span><strong>PLN</strong></li>
        <li><span>Strefa</span><strong>{defaults.timeZone}</strong></li>
      </ul>
      <p className='billing-hint'>Wartości domyślne pochodzą z końca okresu. Zmiany dotyczą wyłącznie tego rozliczenia.</p>
      <form onSubmit={e => { e.preventDefault(); void run(async () => {
        const result = await api<Preview>('/calculations/preview', 'POST', input)
        setPreview(result); setSaveID(requestID()); setStep(3)
      }) }}>
        {defaults.groups.length ? <div className='billing-scroll'><table className='billing-rates'><thead><tr>
            <SortableHeader as='th' field='name' query={ratesSort.query} onSort={ratesSort.onSort}>Grupa</SortableHeader>
            <SortableHeader as='th' field='rateCents' query={ratesSort.query} onSort={ratesSort.onSort} className='billing-num'>PLN netto / maszynę / dzień</SortableHeader>
          </tr></thead><tbody>
          {ratesSort.rows.map(g => <tr key={g.id}><td>{groupLabel(g)}</td><td className='billing-num'><input aria-label={'Stawka grupy ' + groupLabel(g)} type='number' required min={0} max={1000000} step='0.01' disabled={busy} value={input.groupRates[g.id] / 100} onChange={e => change({ groupRates: { ...input.groupRates, [g.id]: Math.round(Number(e.target.value) * 100) } })} /></td></tr>)}
        </tbody></table></div> : <p className='billing-empty'>Brak przypisań maszyn w wybranym okresie.</p>}
        <p className='billing-hint' style={{ marginTop: 8 }}>Stawka 0 wyłącza opłatę. Maszyna należąca do wielu grup jest naliczana raz dziennie według najwyższej stawki.</p>
        <div className='billing-settings' style={{ maxWidth: 520 }}>
          <label>VAT (%)<input type='number' required min={0} max={100} step='0.01' disabled={busy} value={input.vatBasisPoints / 100} onChange={e => change({ vatBasisPoints: Math.round(Number(e.target.value) * 100) })} /></label>
          <label>Termin płatności (dni od wysyłki)<input type='number' required min={0} max={3650} step={1} disabled={busy} value={input.paymentDays} onChange={e => change({ paymentDays: Number(e.target.value) })} /></label>
        </div>
        <label>Uwagi do rozliczenia<textarea maxLength={10000} disabled={busy} value={input.note} onChange={e => change({ note: e.target.value })} /></label>
        <div className='billing-actions'><button type='button' disabled={busy} onClick={() => { setPreview(undefined); setStep(1) }}>Wstecz</button><span className='billing-spacer' /><button className='billing-btn-primary' type='submit' disabled={busy}>Przelicz</button></div>
      </form>
    </section>}
    {step === 3 && preview && input && <section>
      <div className='billing-card-head'><h2>3. Podgląd i zapis</h2></div>
      <ul className='billing-facts'>
        <li><span>Okres (włącznie)</span><strong>{from} – {to}</strong></li>
        <li><span>VAT</span><strong>{input.vatBasisPoints / 100}%</strong></li>
        <li><span>Termin płatności</span><strong>{input.paymentDays} dni od wysyłki</strong></li>
      </ul>
      {!!preview.overlaps.length && <div className='billing-warning' role='status'>
        <p>Wybrany okres pokrywa się z zapisanymi rozliczeniami. Zapis utworzy niezależne rozliczenie.</p>
        <ul>{preview.overlaps.map(o => <li key={o.id}>{o.start} – {o.end} (koniec wyłączony) · {o.source === 'manual' ? 'Na żądanie' : 'Automatyczne'} · {o.id}</li>)}</ul>
      </div>}
      {preview.statement.lines.length ? <div className='billing-scroll'><table><thead><tr>
            <SortableHeader as='th' field='machineName' query={linesSort.query} onSort={linesSort.onSort}>Maszyna</SortableHeader>
            <SortableHeader as='th' field='groupName' query={linesSort.query} onSort={linesSort.onSort}>Grupa</SortableHeader>
            <SortableHeader as='th' field='from' query={linesSort.query} onSort={linesSort.onSort}>Dni płatne (włącznie)</SortableHeader>
            <SortableHeader as='th' field='days' query={linesSort.query} onSort={linesSort.onSort} className='billing-num'>Liczba dni</SortableHeader>
            <SortableHeader as='th' field='rateCents' query={linesSort.query} onSort={linesSort.onSort} className='billing-num'>Stawka netto PLN</SortableHeader>
            <SortableHeader as='th' field='netCents' query={linesSort.query} onSort={linesSort.onSort} className='billing-num'>Netto PLN</SortableHeader>
            <SortableHeader as='th' field='vatCents' query={linesSort.query} onSort={linesSort.onSort} className='billing-num'>VAT PLN</SortableHeader>
            <SortableHeader as='th' field='grossCents' query={linesSort.query} onSort={linesSort.onSort} className='billing-num'>Brutto PLN</SortableHeader>
          </tr></thead><tbody>
        {linesSort.rows.map(l => <tr key={l.id}><td>{l.machineName}</td><td>{l.groupName}</td><td className='billing-nowrap'>{l.from} – {l.to}</td><td className='billing-num'>{l.days}</td><td className='billing-num'>{money(l.rateCents)}</td><td className='billing-num'>{money(l.netCents)}</td><td className='billing-num'>{money(l.vatCents)}</td><td className='billing-num'><strong>{money(l.grossCents)}</strong></td></tr>)}
      </tbody></table></div> : <p className='billing-empty'>Brak płatnych pozycji. Możesz zapisać rozliczenie zerowe.</p>}
      <Totals net={preview.statement.netCents} vat={preview.statement.vatCents} gross={preview.statement.grossCents} />
      {input.note && <><h3>Uwagi</h3><p className='billing-note'>{input.note}</p></>}
      <div className='billing-actions'>
        <button disabled={busy} onClick={() => setStep(2)}>Wstecz</button>
        <span className='billing-spacer' />
        <button className='billing-btn-primary' disabled={busy} onClick={() => void run(async () => {
          const saved = await api<Statement>('/statements', 'POST', { ...input, fingerprint: preview.fingerprint, requestId: saveID })
          await onSaved(saved)
        })}>Zapisz rozliczenie</button>
      </div>
      <p className='billing-hint' style={{ marginTop: 12, textAlign: 'right' }}>Po zapisie można poprawić pozycje i osobno wysłać rozliczenie do klienta.</p>
    </section>}
  </div>
}
